import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr } from "@/lib/api";
import { subscribeTimer, refreshTimer } from "@/lib/timer";
import { Clock, Square } from "lucide-react";
import { toast } from "sonner";

const IDLE_MS_DEFAULT = 30 * 60 * 1000;

export function TimerBanner() {
  const nav = useNavigate();
  const [timer, setTimer] = useState(null);
  const [now, setNow] = useState(Date.now());
  const [stopping, setStopping] = useState(false);
  const [idleMs, setIdleMs] = useState(IDLE_MS_DEFAULT);
  const lastActivity = useRef(Date.now());

  useEffect(() => subscribeTimer(setTimer), []);
  useEffect(() => { api.get("/settings/billing").then((r) => { const m = r.data.idle_minutes ?? 30; setIdleMs(m > 0 ? m * 60000 : 0); }).catch(() => {}); }, []);
  useEffect(() => { if (!timer) return; const iv = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(iv); }, [timer]);
  useEffect(() => {
    const bump = () => { lastActivity.current = Date.now(); };
    const evs = ["mousemove", "keydown", "click", "scroll"];
    evs.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => evs.forEach((e) => window.removeEventListener(e, bump));
  }, []);
  useEffect(() => {
    if (!timer || !idleMs) return;
    const iv = setInterval(async () => {
      if (Date.now() - lastActivity.current >= idleMs) {
        try { const { data } = await api.post(`/tickets/${timer.ticket_id}/timer/stop?reason=idle`); await refreshTimer(); toast.info(`Timer auto-stopped (idle) — logged ${data.minutes}m to #${timer.ticket_number}`); } catch { /* ignore */ }
      }
    }, 30000);
    return () => clearInterval(iv);
  }, [timer, idleMs]);

  if (!timer) return null;
  const s = Math.max(0, Math.floor((now - new Date(timer.started_at).getTime()) / 1000));
  const p = (n) => String(n).padStart(2, "0");
  const elapsed = `${p(Math.floor(s / 3600))}:${p(Math.floor((s % 3600) / 60))}:${p(s % 60)}`;
  const stop = async () => {
    setStopping(true);
    try { const { data } = await api.post(`/tickets/${timer.ticket_id}/timer/stop`); await refreshTimer(); toast.success(`Logged ${data.minutes}m to #${timer.ticket_number}`); }
    catch (e) { toast.error(apiErr(e)); }
    finally { setStopping(false); }
  };

  return (
    <div data-testid="global-timer-banner" className="fixed bottom-4 right-4 z-50 flex items-center gap-2.5 rounded-full bg-slate-900/95 border border-emerald-700/60 shadow-lg shadow-black/40 pl-3 pr-1.5 py-1.5 backdrop-blur">
      <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" /></span>
      <button data-testid="global-timer-goto" onClick={() => nav(`/tickets/${timer.ticket_id}`)} className="flex items-center gap-1.5 text-[12px] text-slate-200 hover:text-cyan-300">
        <Clock size={13} className="text-emerald-400" /> Timer on <span className="font-mono text-cyan-300">#{timer.ticket_number}</span>
        <span className="font-mono text-emerald-400 tabular-nums" data-testid="global-timer-elapsed">{elapsed}</span>
      </button>
      <button data-testid="global-timer-stop" onClick={stop} disabled={stopping} className="inline-flex items-center gap-1 text-[11px] text-rose-200 bg-rose-900/60 hover:bg-rose-800/70 rounded-full px-2.5 py-1 disabled:opacity-50"><Square size={11} /> Stop</button>
    </div>
  );
}
